import { Box } from "@mui/material";

export interface PolicyRichTextViewProps {
    html: string;
}

// Read-only counterpart to the section/subsection MentionInput. Policy content is plain TipTap HTML
// with mentions disabled, so it has no <mention-component> node views that would need a live editor to
// paint. Rendering the stored HTML statically lets a "normal" policy drop dozens of ProseMirror
// instances in view mode. The element CSS mirrors MentionInput's .ProseMirror rules so a section looks
// the same whether it is being viewed or edited.
function PolicyRichTextView({ html }: PolicyRichTextViewProps) {
    return (
        <Box
            className="policy-rich-text-view"
            // These rules mirror MentionInput's .ProseMirror block 1:1 (minus the editor-only scroll/placeholder
            // bits) so a section reads identically whether shown here or in the live editor. fontSize matches the
            // MUI input the editor renders into: 16px for section/subsection content, 14px ("small") for the
            // description. Deliberately no heading/paragraph overrides — the editor leaves those at browser default.
            // The one intentional divergence is the paragraph minHeight: the editor serializes a blank line as an
            // empty <p></p>, which only has height inside ProseMirror because it injects a trailing-break
            // placeholder that is not part of the serialized HTML. Without an explicit height the blank line
            // collapses to nothing the moment the item stops being the live editor.
            sx={(theme) => ({
                fontSize: theme.typography.pxToRem(14),
                color: theme.palette.primary.dark,
                lineHeight: 1.5,
                "& p": { margin: 0, minHeight: "1.5em" },
                "& .mention-link, & a": { color: theme.palette.primary.main, textDecoration: "underline", cursor: "pointer" },
                "& img": { maxWidth: "calc(100% - 4px)", borderRadius: "4px", margin: "8px 2px" },
                "& table": { borderCollapse: "collapse", width: "100%", margin: "12px 0", tableLayout: "fixed" },
                "& th, & td": { border: `1px solid ${theme.palette.divider}`, padding: "4px 8px", verticalAlign: "top", textAlign: "left" },
                "& th": { backgroundColor: theme.palette.action.hover, fontWeight: 600 },
                // A wide imported table has nowhere to go on a phone, so the table becomes its own scroll
                // container - the stored HTML cannot be wrapped in one.
                [theme.breakpoints.down("sm")]: {
                    "& table": { display: "block", overflowX: "auto", width: "max-content", minWidth: "100%", tableLayout: "auto" },
                },
            })}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}

export default PolicyRichTextView;
